import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import ProductHelper, {
  PRODUCT_FACTORIES,
} from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SnackService } from 'src/app/services/snack.service';
import { ContactPickerEvent } from 'src/app/shared/contact-picker/contact-picker-event';
import { CreateViewBaseProjectHelper } from '../client-corner/create-view-base-project/create-view-base-project-helper';
import { SubmitExecutedProjectControllerRequestService } from 'src/app/services/api/submit-executed-project/submit-executed-project-controller-request.service';
import { ApiResponse } from 'src/app/helpers/interfaces/api-response';
import { ApiHelper } from 'src/app/helpers/api-helper';
import { Subject } from 'rxjs';
import { ProjectExtraCompanyContact } from 'src/app/model/project/project-extra-company-contact';
import { CollectionHelper } from 'src/app/helpers/collection-helper';

@Component({
  selector: 'create-old-system-immo-project',
  templateUrl: './create-old-system-immo-project.component.html',
  styleUrls: ['./create-old-system-immo-project.component.scss'],
})
export class CreateOldSystemImmoProjectComponent implements OnInit, OnDestroy {
  private static VALUE_TRUE: (c: AbstractControl) => ValidationErrors = (
    c: AbstractControl
  ) => (c.value === true ? null : [{ key: '' }]);

  @ViewChild(MatStepper)
  matHorizontalStepper: MatStepper;

  init: boolean = false;

  project: Project;
  baseProject: BaseProject;

  contactStepFinished = false;

  contactStepControl: UntypedFormControl;
  configurationStepControl: UntypedFormControl;
  designStepControl: UntypedFormControl;
  placementStepControl: UntypedFormControl;

  submitProjectResult: Subject<void> = new Subject<void>();

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private snackService: SnackService,
    private submitExecutedProjectControllerRequestService: SubmitExecutedProjectControllerRequestService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeProject();
    this.initializeStepControls();
  }

  ngOnDestroy(): void {
    if (this.submitProjectResult != null) {
      this.submitProjectResult.complete();
      this.submitProjectResult = null;
    }
  }

  private initializeProject(): void {
    const project = new Project();
    const baseProject = new BaseProject();
    baseProject.fromOldSystem = true;
    baseProject.status = 'Uitgevoerd';
    project.subProjects = [baseProject];

    const ipBoard = PRODUCT_FACTORIES.IP_BOARD_FACTORY.createProduct(null);
    ipBoard.productAssets = [];
    project.products = [ipBoard];

    this.baseProject = baseProject;
    this.project = project;

    this.init = true;
  }

  private initializeStepControls(): void {
    this.contactStepControl = new UntypedFormControl(
      false,
      CreateOldSystemImmoProjectComponent.VALUE_TRUE
    );
    this.configurationStepControl = new UntypedFormControl(
      false,
      CreateOldSystemImmoProjectComponent.VALUE_TRUE
    );
    this.designStepControl = new UntypedFormControl(
      false,
      CreateOldSystemImmoProjectComponent.VALUE_TRUE
    );
    this.placementStepControl = new UntypedFormControl(
      false,
      CreateOldSystemImmoProjectComponent.VALUE_TRUE
    );
  }

  setContacts(contactPickerEvent: ContactPickerEvent) {
    this.project.projectContacts = contactPickerEvent.projectContacts;
    this.calculateCollaborativeProject();
    this.projectDisplayObservableService
      .getProjectContactsChangedSubscribable()
      .next();
  }

  private calculateCollaborativeProject(): void {
    const involvedCompanies = ProjectHelper.getInvolvedCompanies(this.project);
    this.project.collaborative =
      involvedCompanies != null && involvedCompanies.length > 1;
    if (
      this.project.collaborative &&
      !CollectionHelper.isPopulated(this.project.projectExtraCompanyContacts)
    ) {
      if (this.project.projectExtraCompanyContacts == null) {
        this.project.projectExtraCompanyContacts = [];
      }
      const dummy: ProjectExtraCompanyContact =
        new ProjectExtraCompanyContact();
      dummy.company = '';
      dummy.contact = '';
      this.project.projectExtraCompanyContacts.push(dummy);
    }
  }

  finishContactStep(): void {
    this.contactStepControl.setValue(true);
    this.contactStepFinished = true;
    this.advanceStepper();
  }

  finishConfigurationStep(): void {
    this.configurationStepControl.setValue(true);

    const error =
      CreateViewBaseProjectHelper.validateClientProductConfiguration(
        this.project.products
      );
    if (error != null) {
      this.snackService.showError(error);
      return;
    }

    this.advanceStepper();
  }

  finishDesignStep(): void {
    this.fillOtherSide();
    this.designStepControl.setValue(true);
    this.advanceStepper();
  }

  fillOtherSide() {
    const firstProduct = this.project.products[0];
    if (
      ProductHelper.isIPBoard(firstProduct) &&
      firstProduct.leftRightBoard &&
      firstProduct.boardPart != null &&
      firstProduct.otherSideBoardPart != null
    ) {
      const boardPart = firstProduct.boardPart;
      const otherBoardPart = firstProduct.otherSideBoardPart;
      boardPart.otherCustomText = otherBoardPart.customText;
      boardPart.otherTitleAdjustment = otherBoardPart.titleAdjustment;
      boardPart.otherSideOtherTitle = otherBoardPart.otherTitle;
      boardPart.otherPhoneNumberAdjustment =
        otherBoardPart.phoneNumberAdjustment;
      boardPart.otherSideOtherPhoneNumber = otherBoardPart.otherPhoneNumber;
    }
  }

  finishPlacementStep(): void {
    this.placementStepControl.setValue(true);
    this.advanceStepper();
  }

  private advanceStepper() {
    this.matHorizontalStepper.next();
    window.scrollTo(0, 0);
  }

  submitProject(): void {
    this.submitExecutedProjectControllerRequestService
      .submitExecutedProject(this.project)
      .toPromise()
      .then((response: Project | ApiResponse<Project>) => {
        ApiHelper.handleEntityOrApiResponse(
          response,
          (project: Project) => this.redirectToJobForm(project),
          (apiResponse: ApiResponse<Project>) =>
            this.snackService.showError(
              'Kon job uit oud systeem niet aanmaken: ' + apiResponse.message
            )
        );
      })
      .finally(() => this.submitProjectResult.next());
  }

  private redirectToJobForm(project: Project): void {
    const baseProject = ProjectHelper.getBaseProject(project);
    if (baseProject != null) {
      this.router.navigate(['job', project.id, baseProject.id]);
    } else {
      this.router.navigate(['immo']);
    }
  }
}
